import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  useGetAgentsQuery,
  useSendMessageMutation,
  useLazyGetConversationQuery,
  type ChatMessage,
} from '../../store/api';
import { useAuth, useVoiceChat } from '../../hooks';
import { useWebSocket, type ExchangeEvent } from '../../hooks/useWebSocket';
import { env } from '../../utils/env';
import ThinkingBlock, {
  type ThinkingEntry,
  type PipelineStep,
} from './ThinkingBlock';

/* ---------- styled components ---------- */
const PageBody = styled.div`
  height: calc(100vh - 64px);
  display: flex;
  overflow: hidden;
`;

const PlaygroundSidebar = styled.aside`
  width: 280px;
  border-right: 1px solid var(--border);
  background: var(--sidebar-bg);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  @media (max-width: 768px) { display: none; }
`;

const SidebarHeader = styled.div`
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border);
  h3 { font-size: 0.9375rem; margin-bottom: 0.75rem; }
`;

const AgentSelect = styled.select`
  width: 100%;
  padding: 0.625rem 0.875rem;
  background: var(--input-bg);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.875rem;
  color: var(--text-primary);
  cursor: pointer;
  outline: none;
`;

const NewConvoBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.75rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  background: var(--accent);
  color: white;
  transition: all 0.2s;
  &:hover { background: var(--accent-hover); }
`;

const ChatMain = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const ChatHeader = styled.div`
  padding: 0.75rem 1.5rem;
  border-bottom: 1px solid var(--border);
  background: var(--bg-surface);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SessionBadge = styled.span`
  font-size: 0.6875rem;
  color: var(--text-muted);
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 2px 8px;
  font-family: monospace;
`;

const NewSessionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.4375rem 0.875rem;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid var(--border);
  background: var(--bg-surface);
  color: var(--text-secondary);
  transition: all 0.2s;
  white-space: nowrap;
  &:hover {
    background: var(--accent);
    color: white;
    border-color: var(--accent);
  }
`;

const ChatAgentBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ChatAgentIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--accent-subtle);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.8125rem;
`;

const ChatAgentName = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const ChatAgentEnv = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const ChatMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Msg = styled.div<{ $role: 'user' | 'assistant' | 'system' }>`
  display: flex;
  gap: 0.75rem;
  max-width: 720px;
  animation: fadeIn 0.3s ease;
  ${({ $role }) => $role === 'user' ? 'align-self: flex-end; flex-direction: row-reverse;' : ''}
  ${({ $role }) => $role === 'system' ? 'align-self: center; max-width: 480px;' : ''}
`;

const MsgAvatar = styled.div<{ $bg?: string; $color?: string }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6875rem;
  font-weight: 600;
  flex-shrink: 0;
  background: ${({ $bg }) => $bg || 'var(--accent-subtle)'};
  color: ${({ $color }) => $color || 'var(--accent)'};
`;

const MsgBody = styled.div`
  max-width: 85%;
`;

const MsgBubble = styled.div<{ $role: 'user' | 'assistant' | 'system' }>`
  padding: 0.875rem 1.125rem;
  border-radius: 16px;
  font-size: 0.9375rem;
  line-height: 1.6;
  white-space: pre-wrap;
  ${({ $role }) =>
    $role === 'assistant' ? 'background: var(--bg-surface); border: 1px solid var(--border); color: var(--text-secondary); border-top-left-radius: 4px;'
    : $role === 'user' ? 'background: var(--accent); color: white; border-top-right-radius: 4px;'
    : 'background: var(--info-subtle); border: 1px solid rgba(59, 130, 246, 0.2); color: var(--info); font-size: 0.8125rem; text-align: center; border-radius: 8px;'}
`;

const ChatInputArea = styled.div`
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid var(--border);
  background: var(--bg-surface);
`;

const ChatInputContainer = styled.div`
  max-width: 720px;
  margin: 0 auto;
`;

const ChatInputWrap = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  background: var(--input-bg);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 0.5rem;
  transition: border-color 0.2s;
  &:focus-within { border-color: var(--accent); }
`;

const ChatTextarea = styled.textarea`
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 0.9375rem;
  color: var(--text-primary);
  outline: none;
  resize: none;
  max-height: 160px;
  line-height: 1.5;
  &::placeholder { color: var(--text-muted); }
`;

const SendBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: var(--accent);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  transition: all 0.2s;
  flex-shrink: 0;
  &:hover { background: var(--accent-hover); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const InputHints = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
`;

const InputHint = styled.span`
  font-size: 0.6875rem;
  color: var(--text-muted);
  kbd {
    font-family: inherit;
    padding: 1px 4px;
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: 3px;
    font-size: 0.625rem;
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: var(--text-muted);
  font-size: 0.9375rem;
`;

const VoiceToggleBtn = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.4375rem 0.75rem;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid ${({ $active }) => ($active ? 'var(--accent)' : 'var(--border)')};
  background: ${({ $active }) => ($active ? 'var(--accent-subtle)' : 'var(--bg-surface)')};
  color: ${({ $active }) => ($active ? 'var(--accent)' : 'var(--text-secondary)')};
  transition: all 0.2s;
  white-space: nowrap;
  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

const MicBtn = styled.button<{ $recording?: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${({ $recording }) => ($recording ? 'var(--danger, #ef4444)' : 'var(--bg-elevated)')};
  color: ${({ $recording }) => ($recording ? 'white' : 'var(--text-secondary)')};
  border: 1px solid ${({ $recording }) => ($recording ? 'var(--danger, #ef4444)' : 'var(--border)')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  transition: all 0.2s;
  flex-shrink: 0;
  &:hover {
    background: ${({ $recording }) => ($recording ? 'var(--danger, #dc2626)' : 'var(--accent-subtle)')};
    color: ${({ $recording }) => ($recording ? 'white' : 'var(--accent)')};
    border-color: ${({ $recording }) => ($recording ? 'var(--danger, #dc2626)' : 'var(--accent)')};
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const RecordingIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: var(--danger, #ef4444);
  font-weight: 500;
  animation: pulse 1.5s ease-in-out infinite;
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const VoiceError = styled.div`
  font-size: 0.75rem;
  color: var(--danger, #ef4444);
  margin-top: 0.25rem;
`;

/* ---------- component ---------- */
const ChatPlayground: React.FC = () => {
  const { data: agents = [] } = useGetAgentsQuery();
  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();
  const [fetchConversation] = useLazyGetConversationQuery();
  const { accessToken } = useAuth();

  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [thinkingBlocks, setThinkingBlocks] = useState<ThinkingEntry[]>([]);
  const [pipelineSteps, setPipelineSteps] = useState<PipelineStep[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [exchangeStartTime, setExchangeStartTime] = useState<number | null>(null);
  const [pendingConversationId, setPendingConversationId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const handleVoiceTranscript = useCallback((text: string) => {
    setInput(text);
  }, []);

  const {
    voiceEnabled,
    voiceState,
    error: voiceError,
    toggleVoice,
    startRecording,
    stopRecording,
    speak,
    stopSpeaking,
  } = useVoiceChat({ onTranscript: handleVoiceTranscript });

  const handleWsEvent = useCallback((event: ExchangeEvent) => {
    switch (event.type) {
      case 'exchange.started':
        setIsProcessing(true);
        setExchangeStartTime(Date.now());
        setThinkingBlocks([]);
        setPipelineSteps([]);
        break;

      case 'exchange.thinking':
        setThinkingBlocks((prev) => [
          ...prev,
          {
            id: `t-${Date.now()}`,
            content: event.data?.content || event.data?.text || '',
            timestamp: Date.now(),
          },
        ]);
        break;

      case 'exchange.step.started':
        setPipelineSteps((prev) => [
          ...prev,
          {
            id: event.data?.stepId || `s-${Date.now()}`,
            name: event.data?.name || event.data?.stepName || 'Step',
            status: 'started',
            startedAt: Date.now(),
          },
        ]);
        break;

      case 'exchange.step.completed':
        setPipelineSteps((prev) =>
          prev.map((s) =>
            s.id === (event.data?.stepId || s.id) && s.status === 'started'
              ? { ...s, status: 'completed' as const, completedAt: Date.now() }
              : s,
          ),
        );
        break;

      case 'exchange.completed':
        setIsProcessing(false);
        break;

      case 'exchange.failed':
        setIsProcessing(false);
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: event.data?.error || 'Agent processing failed.' },
        ]);
        break;

      case 'conversation.result': {
        setIsProcessing(false);
        const result = event.data;
        if (result?.response) {
          const responseContent = result.response;
          setMessages((prev) => [
            ...prev,
            { role: 'assistant', content: responseContent },
          ]);
          setPendingConversationId(null);
          if (voiceEnabled && responseContent) speak(responseContent);
        } else if (result?.s3Reference || result?.conversationId) {
          const cid = result.conversationId || pendingConversationId;
          if (cid) {
            fetchConversation(cid)
              .unwrap()
              .then((conv) => {
                const responseContent = conv.result?.response ?? (conv as { response?: string }).response;
                if (responseContent) {
                  setMessages((prev) => [
                    ...prev,
                    { role: 'assistant', content: responseContent },
                  ]);
                  if (voiceEnabled && responseContent) speak(responseContent);
                }
              })
              .catch(() => {});
          }
        }
        break;
      }
    }
  }, [fetchConversation, pendingConversationId, voiceEnabled, speak]);

  const { isConnected, subscribe, unsubscribe } = useWebSocket({
    url: env.WS_ENDPOINT,
    token: accessToken ?? null,
    onEvent: handleWsEvent,
    enabled: !!selectedAgentId,
  });

  useEffect(() => {
    if (agents.length > 0 && !selectedAgentId) {
      setSelectedAgentId(agents[0].agentId);
    }
  }, [agents, selectedAgentId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinkingBlocks]);

  const selectedAgent = agents.find((a) => a.agentId === selectedAgentId);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || !selectedAgentId) return;

    const userMsg: ChatMessage = { role: 'user', content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsProcessing(true);
    setExchangeStartTime(Date.now());
    setThinkingBlocks([]);
    setPipelineSteps([]);

    if (sessionId) {
      subscribe(sessionId, selectedAgent?.agentName);
    }

    try {
      const result = await sendMessage({
        query: text,
        agentId: selectedAgentId,
        sessionId,
        environment: 'sandbox',
      }).unwrap();

      if (result.sessionId) {
        setSessionId(result.sessionId);
        subscribe(result.sessionId, selectedAgent?.agentName);
      }

      if ((result as any).conversationId && (result as any).status === 'processing') {
        setPendingConversationId((result as any).conversationId);
        pollTimerRef.current = setTimeout(() => pollConversation((result as any).conversationId), 30000);
      } else if (result.message) {
        setIsProcessing(false);
        setMessages((prev) => [...prev, result.message]);
        if (voiceEnabled && result.message.content) {
          speak(result.message.content);
        }
      }
    } catch {
      setIsProcessing(false);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' },
      ]);
    }
  };

  const pollConversation = async (conversationId: string, attempt = 0) => {
    if (attempt > 10) {
      setIsProcessing(false);
      return;
    }
    try {
      const conv = await fetchConversation(conversationId).unwrap();
      if (conv.status === 'completed' && conv.result?.response) {
        setIsProcessing(false);
        setPendingConversationId(null);
        const responseContent = conv.result!.response;
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: responseContent },
        ]);
        if (voiceEnabled && responseContent) speak(responseContent);
      } else if (conv.status === 'processing') {
        const delay = Math.min(2000 * Math.pow(1.5, attempt), 15000);
        pollTimerRef.current = setTimeout(
          () => pollConversation(conversationId, attempt + 1),
          delay,
        );
      }
    } catch {
      const delay = Math.min(2000 * Math.pow(1.5, attempt), 15000);
      pollTimerRef.current = setTimeout(
        () => pollConversation(conversationId, attempt + 1),
        delay,
      );
    }
  };

  useEffect(() => {
    return () => clearTimeout(pollTimerRef.current);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewConversation = () => {
    setMessages([]);
    setSessionId(undefined);
    setThinkingBlocks([]);
    setPipelineSteps([]);
    setIsProcessing(false);
    setExchangeStartTime(null);
    setPendingConversationId(null);
    unsubscribe();
    clearTimeout(pollTimerRef.current);
  };

  return (
    <PageBody>
      <PlaygroundSidebar>
        <SidebarHeader>
          <h3>Playground</h3>
          <AgentSelect
            value={selectedAgentId}
            onChange={(e) => {
              setSelectedAgentId(e.target.value);
              handleNewConversation();
            }}
          >
            {agents.map((a) => (
              <option key={a.agentId} value={a.agentId}>
                {a.displayName ?? a.agentName} ({a.agentType})
              </option>
            ))}
          </AgentSelect>
        </SidebarHeader>
        <NewConvoBtn onClick={handleNewConversation}>
          &#x2795; New Conversation
        </NewConvoBtn>
      </PlaygroundSidebar>

      <ChatMain>
        <ChatHeader>
          <ChatAgentBadge>
            <ChatAgentIcon>
              {(selectedAgent?.displayName ?? selectedAgent?.agentName)?.[0] || '?'}
            </ChatAgentIcon>
            <div>
              <ChatAgentName>{selectedAgent?.displayName ?? selectedAgent?.agentName ?? 'Select an agent'}</ChatAgentName>
              <ChatAgentEnv>
                {selectedAgent?.agentType || ''} &middot; Sandbox
              </ChatAgentEnv>
            </div>
          </ChatAgentBadge>
          <HeaderActions>
            {isConnected && (
              <SessionBadge style={{ color: 'var(--success, #22c55e)' }} title="WebSocket connected">
                ● Live
              </SessionBadge>
            )}
            {sessionId && (
              <SessionBadge title={sessionId}>
                session: {sessionId.slice(0, 8)}...
              </SessionBadge>
            )}
            <VoiceToggleBtn
              $active={voiceEnabled}
              onClick={toggleVoice}
              title={voiceEnabled ? 'Disable voice mode' : 'Enable voice mode'}
            >
              {voiceEnabled ? '\u{1F50A}' : '\u{1F507}'} Voice {voiceEnabled ? 'On' : 'Off'}
            </VoiceToggleBtn>
            <NewSessionBtn onClick={handleNewConversation} title="Start a fresh conversation">
              &#x2795; New Session
            </NewSessionBtn>
          </HeaderActions>
        </ChatHeader>

        <ChatMessages>
          {messages.length === 0 && (
            <EmptyState>
              <div style={{ fontSize: '2rem' }}>&#x1F4AC;</div>
              <div>Start a conversation with {selectedAgent?.displayName ?? selectedAgent?.agentName ?? 'an agent'}</div>
            </EmptyState>
          )}

          {messages.map((msg, i) => (
            <React.Fragment key={i}>
              <Msg $role={msg.role === 'system' ? 'system' : msg.role === 'user' ? 'user' : 'assistant'}>
                {msg.role !== 'system' && (
                  <MsgAvatar
                    $bg={msg.role === 'user' ? 'var(--bg-elevated)' : undefined}
                    $color={msg.role === 'user' ? 'var(--text-secondary)' : undefined}
                  >
                    {msg.role === 'user' ? 'U' : (selectedAgent?.displayName ?? selectedAgent?.agentName)?.[0] || 'A'}
                  </MsgAvatar>
                )}
                <MsgBody style={msg.role === 'system' ? { maxWidth: '100%' } : undefined}>
                  <MsgBubble $role={msg.role === 'system' ? 'system' : msg.role === 'user' ? 'user' : 'assistant'}>
                    {msg.content}
                  </MsgBubble>
                </MsgBody>
              </Msg>
              {msg.role === 'user' && isProcessing && i === messages.length - 1 && (
                <ThinkingBlock
                  blocks={thinkingBlocks}
                  steps={pipelineSteps}
                  isActive={isProcessing}
                  startTime={exchangeStartTime}
                />
              )}
            </React.Fragment>
          ))}
          <div ref={messagesEndRef} />
        </ChatMessages>

        <ChatInputArea>
          <ChatInputContainer>
            {voiceState === 'recording' && (
              <RecordingIndicator>&#x1F534; Recording... tap mic to stop</RecordingIndicator>
            )}
            {voiceState === 'transcribing' && (
              <RecordingIndicator style={{ color: 'var(--accent)' }}>
                Transcribing...
              </RecordingIndicator>
            )}
            {voiceState === 'speaking' && (
              <RecordingIndicator
                style={{ color: 'var(--success, #22c55e)', cursor: 'pointer' }}
                onClick={stopSpeaking}
              >
                &#x1F50A; Speaking... click to stop
              </RecordingIndicator>
            )}
            <ChatInputWrap>
              <ChatTextarea
                rows={1}
                placeholder={voiceEnabled ? 'Type or tap the mic...' : 'Type a message...'}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={voiceState === 'recording'}
              />
              {voiceEnabled && (
                <MicBtn
                  $recording={voiceState === 'recording'}
                  title={voiceState === 'recording' ? 'Stop recording' : 'Start recording'}
                  onClick={voiceState === 'recording' ? stopRecording : startRecording}
                  disabled={voiceState === 'transcribing' || voiceState === 'speaking' || isProcessing}
                >
                  {voiceState === 'recording' ? '\u23F9' : '\u{1F3A4}'}
                </MicBtn>
              )}
              <SendBtn
                title="Send"
                onClick={handleSend}
                disabled={isSending || !input.trim() || voiceState === 'recording'}
              >
                &#x2191;
              </SendBtn>
            </ChatInputWrap>
            {voiceError && <VoiceError>{voiceError}</VoiceError>}
            <InputHints>
              <InputHint><kbd>Enter</kbd> to send</InputHint>
              <InputHint><kbd>Shift+Enter</kbd> for new line</InputHint>
              {voiceEnabled && <InputHint>&#x1F3A4; Mic to speak</InputHint>}
            </InputHints>
          </ChatInputContainer>
        </ChatInputArea>
      </ChatMain>
    </PageBody>
  );
};

export default ChatPlayground;
